'use client';

import { useEffect, useState } from 'react';

/**
 * Effect-based `window.location` subscription (patches pushState/replaceState
 * and listens for popstate). Use this instead of `usePathname()`/
 * `useSearchParams()` when the URL only drives EFFECTS, not render output:
 * unlike the Next hooks it is not URL data, so components using it stay legal
 * in prerendered shells and Suspense fallbacks on routes without statically
 * known params (e.g. the [...catchAll] 404 route). Returns empty strings on
 * the server and until the mount effect fires.
 *
 * Extracted from product-main.tsx; shared by the PDP and the site header.
 */
export function useBrowserLocation() {
	const [location, setLocation] = useState({ pathname: '', search: '' });

	useEffect(() => {
		let isMounted = true;

		const updateLocation = () => {
			if (!isMounted) return;

			setLocation((currentLocation) => {
				const nextLocation = {
					pathname: window.location.pathname,
					search: window.location.search,
				};

				if (
					currentLocation.pathname === nextLocation.pathname &&
					currentLocation.search === nextLocation.search
				) {
					return currentLocation;
				}

				return nextLocation;
			});
		};

		const scheduleLocationUpdate = () => {
			queueMicrotask(updateLocation);
		};

		updateLocation();

		const originalPushState = window.history.pushState;
		const originalReplaceState = window.history.replaceState;

		window.history.pushState = function pushState(...args) {
			originalPushState.apply(window.history, args);
			scheduleLocationUpdate();
		};

		window.history.replaceState = function replaceState(...args) {
			originalReplaceState.apply(window.history, args);
			scheduleLocationUpdate();
		};

		window.addEventListener('popstate', updateLocation);

		return () => {
			isMounted = false;
			window.history.pushState = originalPushState;
			window.history.replaceState = originalReplaceState;
			window.removeEventListener('popstate', updateLocation);
		};
	}, []);

	return location;
}
